import { useEffect, useState } from 'react'
import { msg, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Box, Button, MenuItem, Popover, Stack, TextField } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/'
import { getNotificationMeta } from './notification-meta.util'
import {
  EMPTY_NOTIFICATION_FILTERS,
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_DATABASE_OPTIONS,
} from './notification-center.const'

const FILTERABLE_CATEGORIES = Object.values(NOTIFICATION_CATEGORIES).filter(c => c !== NOTIFICATION_CATEGORIES.OTHER)

function FilterSelect({ label, value, onChange, children }) {
  const { _ } = useLingui()
  return (
    <TextField select size="small" fullWidth label={label} value={value} onChange={event => onChange(event.target.value)}>
      <MenuItem value=""><em>{_(msg`Any`)}</em></MenuItem>
      {children}
    </TextField>
  )
}

/**
 * Filter dropdown: date range, user, action type, database.
 * @param {{ anchorEl: HTMLElement|null, filters: object, users: Array<{id: number, username: string}>, onApply: (f: object) => void, onClose: () => void }} props
 */
export function NotificationFilterPopover({ anchorEl, filters, users, onApply, onClose }) {
  const { _ } = useLingui()
  const [draft, setDraft] = useState(filters)
  const setField = field => value => setDraft(previous => ({ ...previous, [field]: value }))

  useEffect(() => { if (anchorEl) setDraft(filters) }, [anchorEl, filters])

  return (
    <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={onClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
      <Stack spacing={1.5} sx={{ p: 2, width: 300 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker label={_(msg`From`)} value={draft.dateFrom} onChange={setField('dateFrom')} slotProps={{ textField: { size: 'small' } }} />
          <DatePicker label={_(msg`To`)} value={draft.dateTo} onChange={setField('dateTo')} slotProps={{ textField: { size: 'small' } }} />
        </LocalizationProvider>
        <FilterSelect label={_(msg`User`)} value={draft.userId} onChange={setField('userId')}>
          {(users ?? []).map(user => <MenuItem key={user.id} value={user.id}>{user.username}</MenuItem>)}
        </FilterSelect>
        <FilterSelect label={_(msg`Action type`)} value={draft.category} onChange={setField('category')}>
          {FILTERABLE_CATEGORIES.map(category => (
            <MenuItem key={category} value={category}>{_(getNotificationMeta({ category }).title)}</MenuItem>
          ))}
        </FilterSelect>
        <FilterSelect label={_(msg`Database`)} value={draft.organization} onChange={setField('organization')}>
          {NOTIFICATION_DATABASE_OPTIONS.map(option => <MenuItem key={option.value} value={option.value}>{_(option.label)}</MenuItem>)}
        </FilterSelect>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button size="small" onClick={() => onApply(EMPTY_NOTIFICATION_FILTERS)}><Trans>Reset</Trans></Button>
          <Button size="small" variant="contained" onClick={() => onApply(draft)}><Trans>Apply</Trans></Button>
        </Box>
      </Stack>
    </Popover>
  )
}
